import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { ApiService } from "./api.service";
import { LEAGUE_ID } from "src/app/components/aau-district-directors/aau-district-directors.service";


interface ATHMembership {
  expanded?: boolean;
  additionalText1: string; //"AAU_NON_ATHLETE_MEMBERSHIP"
  amount: number;
  areaCode: number;
  countyName: string;
  coverage: "EXTENDED" | "STANDARD";
  createDate: string; //"2025-08-05 11:31:06"
  districtCode: string;//"AD"
  districtName: string;
  endDate: string; //"2026-08-31"
  externalCode: string; //"12345"
  fips: number;
  id: number;
  isNonAthlete: boolean;
  lat: number;
  leagueSportId: number;
  lon: number;
  membershipNumber: string;
  membershipProgram: "YOUTH" | "ADULT";
  membershipRegistrator: string;
  membershipSystem: string;
  personId: number;
  ratingSystemId: number;
  registrationDate: string; //"2025-08-05 11:31:06"
  startDate: string;// "2025-09-01"
  status: "APPROVED" | "REJECTED" | "PENDING" | "BACKGROUND_CHECK_PENDING";
}

export interface PersonAccount {
  expanded?: boolean;
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  middleName: string;
  doNotUseMiddleName: boolean;
  gender: "MALE" | "FEMALE";
  birthDate: string; //"1988-10-31",
  createDate: string; //"2025-08-12T23:28:23+0000",
  country: {
    id: number;
    name: string;
    code: string,
    codeAlpha3: string;
  };
  state?: {
    id: number;
    name: string;
    code: string;
    countryId: number;
  };
  city: string;
  address: string;
  zipCode: string;
  phoneNumber: string;
  phoneCountryCode: string;
  phoneCountry: string;
  club?: {
    id: number;
    name: string;
    country: {
      id: number;
      name: string;
      code: string;
      codeAlpha3: string;
    };
    state: {
      id: number;
      name: string;
      code: string;
      countryId: number;
    },
    city: string;
    address: string;
    zipCode: string;
    phoneNumber: string;
    email: string;
    isApproved: string; //"APPROVED";
    externalId: string;//"W4T9DB",
    integrationTyp: string; // "AAU_CLUB"
  },
  relation: string,
  isSelected: boolean,
  primarySport?: {
    id: number,
    code: string,
    name: string,
    // "logo": null,
    // "logoUrl": null,
    // "displayOrder": 11,
    // "isNCSASport": true,
    // "isEnabled": true,
    // "youthAgeLimit": 17,
    // "restrictionGender": null,
    // "minAllowedAge": null,
    // "maxAllowedAge": null,
    // "standardCoverageDisabled": false,
    // "extendedCoverageDisabled": false,
    // "ageGroup": "BOTH"
  },
  memberships?: ATHMembership[],
  isMyChild: boolean,
  defaultMembershipProgram: "ADULT" | "YOUTH";
  autoRenewMembership?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class AAUAccountMembershipService {

  search = "";
  page: number = 0;
  pageSize: number = 10;


  readonly nonAthleteEventId = 334300;
  readonly athleteEventId = 334299;

  readonly eventId: number = this.athleteEventId;


  baseUrl: string = environment.APIURL;
  uvUrl = environment.devUvLink;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  aauAccountPerson$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  aauPersonsWithMemberships$: BehaviorSubject<PersonAccount[]> = new BehaviorSubject<PersonAccount[]>([]);

  constructor(private http: HttpClient, private api: ApiService) { }


  getActiveAthletes(leagueId: number): PersonAccount[] {
    return this.aauPersonsWithMemberships$.getValue()?.filter(
      (x: PersonAccount) =>
        x.memberships?.some(m => !m.isNonAthlete && m.ratingSystemId === leagueId) // has at least one athlete membership
    );
  }

  getActiveNonAthletes(leagueId: number): PersonAccount[] {
    return this.aauPersonsWithMemberships$.getValue()?.filter(
      (x: PersonAccount) =>
        x.memberships?.some(m => m.isNonAthlete && m.ratingSystemId === leagueId) // has at least one non-athlete membership
    );
  }

  getLatestActiveMembershipEndDate(
    leagueId: number,
    memberships: ATHMembership[],
    isNonAthlete: boolean
  ): ATHMembership | null {
    const filtered = memberships.filter(
      m =>
        m.isNonAthlete === isNonAthlete &&
        m.ratingSystemId === leagueId &&
        (m.status === "APPROVED" || m.status === "PENDING" || m.status === "BACKGROUND_CHECK_PENDING")
    );

    if (filtered.length === 0) return null;

    return filtered.reduce((max, m) =>
      new Date(m.endDate) > new Date(max.endDate) ? m : max
    );
  }


  async haveSomeAccount() {
    this.loading$.next(true);
    try {
      let data: any = await firstValueFrom(
        this.api.getPersons("", 0, 3000, -1, this.eventId, null, false, false)
      );

      const personsWithAAUMemberships = [];

      if (data && data.items?.length) {
        for (const p of data.items) {
          if (p && p.memberships?.length) {
            for (const m of p.memberships) {
              if ((m.status === "PENDING" || m.status === "APPROVED" || m.status === "BACKGROUND_CHECK_PENDING" || m.status === "NCSI_RESUBMITTED")) {
                personsWithAAUMemberships.push(p);
                break;
              }
            }
          }
        }
      }

      this.aauPersonsWithMemberships$.next(personsWithAAUMemberships);
      if (personsWithAAUMemberships.length) {
        this.aauAccountPerson$.next(true);
      } else {
        this.aauAccountPerson$.next(false);
      }

    } catch (e) {
      console.error(e);
    } finally {
      this.loading$.next(false);
    }
  }



}
